// <copyright file="v6-customer-code-flushed.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { customerCodeFlushedPageEvent } from '../quotes/data/v6/quote-item-version-recheck';
import { v6Config, v6SupportsVersion, v6VersionMap } from '../../webmodule-common/interop/interfaces/v6-config';

/** The v6-customer-code-flushed event's data (SSESupplierCustomerCodeFlushed). */
export interface CustomerCodeFlushedEvent {
  supplierId: string;
}

/**
 * The server flushed one or more of this tenant's supplier customer codes (SUPVER-010): reload the suppliers so the
 * settings are fresh, then drop each named supplier's entries from the persistent V6 caches, whose version stamp may
 * not have moved. Other suppliers' entries are kept.
 * Open quotes are then told, so they recheck their items. Unlike a v6-service-status event, V6 did not restart, so the
 * bootloader is not marked stale. A frameconfig older than the flush keeps its caches until their stamp moves.
 * @returns false when the forced reload failed (the frameconfig sets `supplierReloadFailed`): nothing is flushed or
 * rechecked, because the settings are still the old ones.
 */
export async function refreshAfterCustomerCodeFlush(
  supplierIds: Iterable<string>,
  reloadSuppliers: () => Promise<unknown>,
  forceFlushCaches: ((supplierId: string) => Promise<void>) | undefined
): Promise<boolean> {
  globalThis.v6config = globalThis.v6config ?? {};
  globalThis.v6config.forceLoad = true;
  globalThis.v6config.supplierReloadFailed = false;
  try {
    await reloadSuppliers();
  } finally {
    globalThis.v6config.forceLoad = false;
  }
  if (globalThis.v6config.supplierReloadFailed) return false;
  const flushed = [...supplierIds];
  if (forceFlushCaches) {
    for (const supplierId of flushed) {
      await forceFlushCaches(supplierId);
    }
  }
  // Open quotes recheck their items against the reloaded suppliers (SUPVER-010).
  globalThis.dispatchEvent(new CustomEvent(customerCodeFlushedPageEvent, { detail: { supplierIds: flushed } }));
  return true;
}

/**
 * The suppliers named by customer-code-flushed events and not yet refreshed. A supplier leaves only when a refresh
 * that included it completed, so a failed reload (offline, 5xx) loses nothing: the next run takes it again.
 */
export class PendingCustomerCodeFlushes {
  private readonly pending = new Set<string>();

  add(supplierId: string) {
    this.pending.add(supplierId);
  }

  get hasPending(): boolean {
    return this.pending.size > 0;
  }

  /** @returns false when the refresh could not be completed and the suppliers are still pending. */
  async run(
    reloadSuppliers: () => Promise<unknown>,
    forceFlushCaches: ((supplierId: string) => Promise<void>) | undefined
  ): Promise<boolean> {
    const supplierIds = [...this.pending];
    if (supplierIds.length === 0) return true;
    let done = false;
    try {
      done = await refreshAfterCustomerCodeFlush(supplierIds, reloadSuppliers, forceFlushCaches);
    } catch (e) {
      console.warn('Refresh after a customer code flush failed; it will be retried', e);
    }
    if (done) supplierIds.forEach(supplierId => this.pending.delete(supplierId));
    return done;
  }
}

/** The loaded frameconfig's forced cache flush, or undefined when it predates it. */
export function frameconfigForceFlushCaches(): ((supplierId: string) => Promise<void>) | undefined {
  return v6SupportsVersion(v6VersionMap.hasCustomerCodeFlush)
    ? supplierId => v6Config().forceFlushCaches(supplierId)
    : undefined;
}
